import type { FunctionComponent, ReactNode } from 'react'
import styled, { css } from 'styled-components'
import { Link as RouterLink, To } from 'react-router'
import Icon from '../../Icon/Icon'
import Chevron from '../../../assets/icons/chevronDown.svg'

export enum Variant {
  Inline = 'ínline',
  Card = 'card',
  Basic = 'basic',
}

const StyledNativeLink = styled.a<{
  $variant?: Variant
  $inList?: boolean
  $hasIcon?: boolean
}>`
  background-color: transparent;
  font-size: inherit;
  text-decoration: underline;
  padding: 0;
  color: var(--blue);
  text-underline-offset: 4px;

  &:hover {
    text-decoration-thickness: 0.125rem;
  }
  ${({ $variant }) =>
    $variant === Variant.Inline &&
    css`
      display: inline-flex;
    `}
  ${({ $hasIcon }) =>
    $hasIcon &&
    css`
      display: inline-flex;
      align-items: center;
    `}
  ${({ $inList }) =>
    $inList &&
    css`
      font-weight: 700;
      color: var(--dark);
      text-decoration: none;
      svg {
        margin-right: 0.25rem;
      }
      &:hover {
        color: var(--red);
        svg {
          fill: var(--red);
        }
      }
    `}
  ${({ $variant }) =>
    $variant === Variant.Card &&
    css`
      color: var(--dark);
      text-decoration: none;
      &:focus,
      &:hover {
        color: inherit;
        text-decoration: none;
      }
    `}
  ${({ $variant }) =>
    $variant === Variant.Basic &&
    css`
      color: var(--dark);
      text-decoration: none;
      span svg path {
        fill: var(--dark);
      }
      &:focus,
      &:hover {
        color: var(--dark);
        text-decoration: underline;
        span svg path {
          fill: var(--dark);
        }
      }
    `}
`

interface LinkProps {
  children: ReactNode
  href?: string
  icon?: ReactNode
  inList?: boolean
  onClick?: (event: any) => void
  variant?: Variant
  state?: unknown
  style?: object
  target?: string
  to?: To
  rel?: string
  title?: string
}

const LinkComponent = ({
  hasIcon,
  to,
  inList,
  variant,
  ...otherProps
}: LinkProps & { hasIcon?: boolean }) => {
  if (to) {
    return (
      <StyledNativeLink
        $inList={inList}
        $variant={variant}
        $hasIcon={hasIcon}
        as={RouterLink}
        {...otherProps}
        to={to}
      />
    )
  }

  return (
    <StyledNativeLink
      $inList={inList}
      $variant={variant}
      $hasIcon={hasIcon}
      as={otherProps.href ? undefined : 'button'}
      rel="noopener noreferrer"
      target="_blank"
      {...otherProps}
    />
  )
}

const Link: FunctionComponent<LinkProps> = ({ children, icon: iconProp, ...otherProps }) => {
  const icon = otherProps.inList ? (
    <Icon size={12} rotate={270}>
      <Chevron />
    </Icon>
  ) : (
    iconProp
  )

  return (
    <LinkComponent {...otherProps} hasIcon={!!icon}>
      {icon}
      {children}
    </LinkComponent>
  )
}

export default Link
